import React, { useEffect, useRef, useState } from "react";
import useTranslationHook from "../hooks/useTranslationHook";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { handleSellerConfirmModal } from "../../redux/slice/modalSlice";
import { fetchPropertyDetail } from "../../redux/action/sellerAction";
import { fetchEmsignerStatus } from "../../redux/action/buyerAction";
import { toast } from "react-toastify";
import { errToast, makeFullName } from "../../utils";
import EmsignerIframe from "./EmsignerIframe";
import Shimmer from "./shimmer/Shimmer";
import { updateBuyerRegistrationField } from "../../redux/slice/buyerSlice";

function SellerConfirmPurchase() {
    const { t } = useTranslationHook();
    const show = useSelector((state) => state.modal.sellerConfirmPurchaseModal);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { id: propertyId } = useParams();
    const [isSubmiting, setIsSubmiting] = useState(false);
    const emsignerReturnedRef = useRef(false);

    const propertyData = useSelector(
        (state) => state.seller.property.propertyData
    );
    const { first_name, last_name } =
        useSelector((state) => state.auth.user) || {};

    const handleClose = () => {
        dispatch(handleSellerConfirmModal(false));
    };

    useEffect(() => {
        document.body.classList.toggle("modal-open", show);
        if (show) emsignerReturnedRef.current = false;
        return () => {
            document.body.classList.remove("modal-open");
        };
    }, [show]);

    useEffect(() => {
        if (!show) return;
        const rawSearch = window.location.search;
        const normalized =
            rawSearch.slice(0, 1) + rawSearch.slice(1).replace(/\?/g, "&");
        const urlParams = new URLSearchParams(normalized);
        const workflowid = urlParams.get("workflowid");
        if (!workflowid) return;

        emsignerReturnedRef.current = true;

        urlParams.delete("workflowid");
        urlParams.delete("referenceno");
        const cleanSearch = urlParams.toString()
            ? `?${urlParams.toString()}`
            : "";
        navigate(
            { pathname: location.pathname, search: cleanSearch },
            { replace: true, state: location.state }
        );

        const runSubmit = async () => {
            setIsSubmiting(true);
            try {
                const verifyRes = await dispatch(
                    fetchEmsignerStatus(propertyId, "sale_purchase")
                );
                const status = verifyRes?.data?.user_sing_status;
                if (status === "completed" || status === "pending") {
                    // making the bidder registration disabled, just after signing
                    dispatch(
                        updateBuyerRegistrationField({
                            field: "agreementSigned",
                            value: true,
                        })
                    );
                    toast.success("Agreement has been signed successfully");
                    dispatch(fetchPropertyDetail(propertyData?.id));
                } else {
                    toast.info(verifyRes?.data?.msg);
                }
            } catch (err) {
                errToast(err);
            } finally {
                handleClose();
                setIsSubmiting(false);
            }
        };

        runSubmit();
        //eslint-disable-next-line
    }, [location.search, show]);

    if (!show) {
        return <></>;
    }

    return createPortal(
        <div
            className={`modal fade ${show ? "show" : ""} purchase-modal`}
            tabIndex="-1"
        >
            <div className="modal-dialog modal-fullscreen modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header mb-0">
                        <h5 className="modal-title">
                            {t("Purchase Agreement")}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="modal-body">
                        {isSubmiting ? (
                            <Shimmer
                                type="rectangle"
                                width="100%"
                                height="10vh"
                                borderRadius="5%"
                            />
                        ) : (
                            show &&
                            !emsignerReturnedRef.current && (
                                <EmsignerIframe
                                    propertyId={propertyId}
                                    agreementType="sale_purchase"
                                    seller={true}
                                />
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.getElementById("root")
    );
}

export default SellerConfirmPurchase;
